import React, { useState, useEffect } from 'react';
import { Cookie, X, Settings } from 'lucide-react';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookieConsent: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always true and disabled
    analytics: true,
    marketing: true
  });

  useEffect(() => {
    // Load saved preferences if available to set initial state of toggles
    const saved = localStorage.getItem('talepia_cookie_consent');
    if (saved) {
        try {
            const parsed = JSON.parse(saved);
            // Validate parsed data structure
            if (parsed && typeof parsed === 'object' && typeof parsed.necessary === 'boolean') {
                setPreferences(parsed);
            } else {
                // Invalid data structure - remove it
                console.warn('⚠️ Invalid cookie consent data, removing...');
                localStorage.removeItem('talepia_cookie_consent');
                setIsVisible(true);
            }
        } catch(e) {
            // Parse error - remove corrupt data
            console.warn('⚠️ Corrupt cookie consent data, removing...', e);
            localStorage.removeItem('talepia_cookie_consent');
            setIsVisible(true);
        }
    } else {
        // Show banner if no consent found
        const timer = setTimeout(() => setIsVisible(true), 1500);
        return () => clearTimeout(timer);
    }
  }, []);

  // Listen for custom event to open settings from Footer
  useEffect(() => {
    const handleOpenSettings = () => {
        const saved = localStorage.getItem('talepia_cookie_consent');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                if (parsed && typeof parsed === 'object' && typeof parsed.necessary === 'boolean') {
                    setPreferences(parsed);
                } else {
                    localStorage.removeItem('talepia_cookie_consent');
                }
            } catch(e) {
                localStorage.removeItem('talepia_cookie_consent');
            }
        }
        setShowSettings(true);
    };
    window.addEventListener('open_cookie_settings', handleOpenSettings);
    return () => window.removeEventListener('open_cookie_settings', handleOpenSettings);
  }, []);

  const handleAcceptAll = () => {
    const newPrefs = { necessary: true, analytics: true, marketing: true };
    setPreferences(newPrefs);
    localStorage.setItem('talepia_cookie_consent', JSON.stringify(newPrefs));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('talepia_cookie_consent', JSON.stringify(preferences));
    setIsVisible(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const newPrefs = { necessary: true, analytics: false, marketing: false };
    setPreferences(newPrefs);
    localStorage.setItem('talepia_cookie_consent', JSON.stringify(newPrefs));
    setIsVisible(false);
  };

  // SETTINGS MODAL
  if (showSettings) {
      return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm animate-in fade-in">
           <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-gray-100 dark:border-gray-700">
               <div className="flex justify-between items-center mb-6">
                   <h3 className="text-xl font-bold dark:text-white flex items-center"><Cookie className="w-5 h-5 mr-2 text-indigo-500"/> Çerez Tercihleri</h3>
                   <button onClick={() => setShowSettings(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"><X className="w-5 h-5 dark:text-white"/></button>
               </div>
               
               <div className="space-y-4 mb-8">
                   {/* Necessary */}
                   <div className="flex items-start justify-between p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700">
                       <div className="pr-4">
                           <h4 className="font-bold text-gray-900 dark:text-white text-sm">Zorunlu Çerezler</h4>
                           <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Sitenin temel fonksiyonları için gereklidir. Kapatılamaz.</p>
                       </div>
                       <div className="relative inline-flex items-center cursor-not-allowed opacity-50">
                           <input type="checkbox" checked disabled className="sr-only peer" />
                           <div className="w-11 h-6 bg-indigo-600 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                       </div>
                   </div>

                   {/* Analytics */}
                   <div className="flex items-start justify-between p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                       <div className="pr-4">
                           <h4 className="font-bold text-gray-900 dark:text-white text-sm">Analitik Çerezler</h4>
                           <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Sitemizi nasıl kullandığınızı anlamamıza ve geliştirmemize yardımcı olur.</p>
                       </div>
                       <label className="relative inline-flex items-center cursor-pointer">
                           <input type="checkbox" checked={preferences.analytics} onChange={e => setPreferences({...preferences, analytics: e.target.checked})} className="sr-only peer" />
                           <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                       </label>
                   </div>

                   {/* Marketing */}
                   <div className="flex items-start justify-between p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                       <div className="pr-4">
                           <h4 className="font-bold text-gray-900 dark:text-white text-sm">Pazarlama Çerezleri</h4>
                           <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">İlgi alanlarınıza göre kişiselleştirilmiş reklamlar sunmak için kullanılır.</p>
                       </div>
                       <label className="relative inline-flex items-center cursor-pointer">
                           <input type="checkbox" checked={preferences.marketing} onChange={e => setPreferences({...preferences, marketing: e.target.checked})} className="sr-only peer" />
                           <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                       </label>
                   </div>
               </div>

               <div className="flex gap-3">
                   <button onClick={handleSavePreferences} className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold transition-all shadow-lg">Seçimi Kaydet</button>
               </div>
           </div>
        </div>
      );
  }

  // BANNER VIEW (Only visible if not consented yet)
  if (!isVisible) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-[28rem] z-[90] animate-in slide-in-from-bottom-10 duration-500">
      <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-700 relative">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 bg-indigo-50 dark:bg-indigo-900/30 rounded-full flex items-center justify-center shrink-0">
            <Cookie className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h4 className="font-bold text-gray-900 dark:text-white text-base mb-1">Çerez Tercihleri</h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              Deneyiminizi iyileştirmek, site trafiğini analiz etmek ve size özel içerikler sunmak için çerezler kullanıyoruz.
            </p>
          </div>
        </div>
        
        <div className="flex flex-col gap-2">
           <div className="flex gap-2">
              <button 
                onClick={handleAcceptAll}
                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold py-3 rounded-xl transition-colors shadow-md"
              >
                Hepsini Kabul Et
              </button>
              <button 
                onClick={handleRejectAll}
                className="flex-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs font-bold py-3 rounded-xl transition-colors"
              >
                Reddet
              </button>
           </div>
           <button 
              onClick={() => setShowSettings(true)}
              className="w-full text-indigo-600 dark:text-indigo-400 text-xs font-bold py-2 hover:underline flex items-center justify-center"
            >
              <Settings className="w-3 h-3 mr-1"/> Detaylı Ayarlar
           </button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;